import { useState, useRef } from "react";
import * as api from "../../apiService";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const STATUS_STYLE = {
  ACTIVE:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", label: "Active"  },
  IGNORED: { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", label: "Ignored" },
  PENDING: { bg: "#fffbeb", color: "#b45309", border: "#fde68a", label: "Pending" },
};

const FILTERS = ["ALL", "PENDING", "ACTIVE", "IGNORED"];

const th = { textAlign: "left", padding: "8px 10px", fontSize: 12, fontWeight: 600, color: "#374151" };
const td = { padding: "8px 10px", verticalAlign: "middle" };

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.PENDING;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 4, padding: "2px 8px", fontSize: 11, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function CompanySection({ company, users, onAssign, onIgnore, onReset, saving }) {
  const [filter, setFilter] = useState("ALL");

  const counts = FILTERS.slice(1).reduce((acc, f) => {
    acc[f] = company.groups.filter(g => g.status === f).length;
    return acc;
  }, {});

  const visible = company.groups.filter(g => filter === "ALL" || g.status === filter);

  return (
    <div style={{ marginBottom: 32 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{company.businessUnit}</span>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>{company.companyName}</span>
        <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
          {FILTERS.map(f => (
            <button key={f}
              onClick={() => setFilter(f)}
              style={{
                fontSize: 11, padding: "3px 10px", borderRadius: 4, cursor: "pointer",
                background: filter === f ? "#1d4ed8" : "#f9fafb",
                color:      filter === f ? "white"    : "#374151",
                border:     filter === f ? "1px solid #1d4ed8" : "1px solid #e5e7eb",
                fontWeight: filter === f ? 600 : 400,
              }}>
              {f === "ALL" ? `All (${company.groups.length})` : `${f[0]}${f.slice(1).toLowerCase()} (${counts[f]})`}
            </button>
          ))}
        </div>
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
              <th style={th}>Group (Tally)</th>
              <th style={th}>Balance</th>
              <th style={th}>Status</th>
              <th style={th}>Assigned To</th>
              <th style={{ ...th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} style={{ ...td, textAlign: "center", color: "#9ca3af", padding: 20 }}>
                  No groups in this view.
                </td>
              </tr>
            )}
            {visible.map(g => {
              const key = `${company.companyId}::${g.groupName}`;
              const isSaving = saving === key;
              return (
                <tr key={g.groupName} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ ...td, fontWeight: 500 }}>
                    {g.groupName}
                    {g.stale && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: "#9ca3af" }}>not in Tally</span>
                    )}
                  </td>
                  <td style={{ ...td, fontSize: 12, color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>
                    {g.balance ? formatBalance(g.balance) : "—"}
                  </td>
                  <td style={td}><StatusBadge status={g.status} /></td>
                  <td style={td}>
                    {g.status === "IGNORED"
                      ? <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                      : (
                        <select
                          value={g.userEmail ?? ""}
                          disabled={isSaving}
                          onChange={e => onAssign(company.companyId, g.groupName, e.target.value)}
                          style={{
                            fontSize: 12, padding: "4px 8px", borderRadius: 4,
                            border: "1px solid #d1d5db", background: "white",
                            color: g.userEmail ? "#111827" : "#9ca3af",
                            minWidth: 180,
                          }}>
                          <option value="">— Select salesperson —</option>
                          {users.map(u => (
                            <option key={u.email} value={u.email}>{u.name}</option>
                          ))}
                        </select>
                      )
                    }
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      {g.status !== "IGNORED" && (
                        <button
                          disabled={isSaving}
                          onClick={() => onIgnore(company.companyId, g.groupName)}
                          style={{
                            fontSize: 11, padding: "3px 10px", borderRadius: 4, cursor: "pointer",
                            background: "#f9fafb", color: "#6b7280", border: "1px solid #e5e7eb",
                          }}>
                          Ignore
                        </button>
                      )}
                      {g.status !== "PENDING" && (
                        <button
                          disabled={isSaving}
                          onClick={() => onReset(company.companyId, g.groupName)}
                          style={{
                            fontSize: 11, padding: "3px 10px", borderRadius: 4, cursor: "pointer",
                            background: "#f9fafb", color: "#374151", border: "1px solid #e5e7eb",
                          }}>
                          Reset
                        </button>
                      )}
                      {isSaving && <span style={{ fontSize: 11, color: "#9ca3af" }}>Saving…</span>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatBalance(val) {
  const n = parseFloat(val);
  if (isNaN(n) || n === 0) return "0.00";
  const abs = Math.abs(n).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `AED ${abs} Dr` : `AED ${abs} Cr`;
}

function buildMonthOptions() {
  const now = new Date();
  const opts = [];
  for (let i = 0; i <= 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return opts;
}

function TallyLedgerCachePanel({ showToast, onRefreshed }) {
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await api.refreshCrmCache();
      await onRefreshed();
      showToast("Tally ledger cache refreshed successfully.");
    } catch (e) {
      showToast(`Refresh failed: ${e.message}`, false);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Tally Ledger Cache</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Force re-fetch all customer ledger data from Tally — groups, balances, and customer list. Use this if new customers or balance changes are not appearing.
      </p>
      <button className="btn" onClick={handleRefresh} disabled={refreshing} style={{ whiteSpace: "nowrap" }}>
        {refreshing ? "Refreshing…" : "Refresh Tally Ledger Cache"}
      </button>
    </div>
  );
}

function SyncInvoiceDatesPanel({ showToast }) {
  const [syncing, setSyncing] = useState(false);

  async function handleSync() {
    setSyncing(true);
    try {
      await api.syncVoucherDates();
      showToast("Last invoice dates synced successfully.");
    } catch (e) {
      showToast(`Sync failed: ${e.message}`, false);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Last Invoice Date Sync</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Pulls the latest sales voucher dates from Tally and updates the Active / Inactive status for all customers.
      </p>
      <button className="btn" onClick={handleSync} disabled={syncing} style={{ whiteSpace: "nowrap" }}>
        {syncing ? "Syncing…" : "Sync Last Invoice Dates"}
      </button>
    </div>
  );
}

function CreditDataSyncPanel({ showToast }) {
  const [syncing, setSyncing] = useState(false);

  async function handleSync() {
    setSyncing(true);
    try {
      await api.syncCreditData();
      showToast("Credit limit & period synced from Tally successfully.");
    } catch (e) {
      showToast(`Sync failed: ${e.message}`, false);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Credit Limit & Period Sync</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Pull credit limit and credit days from the CCReport TDL (CreditControl_v3.tdl) and update all customer records. Normally runs automatically at midnight — use this for an immediate refresh.
      </p>
      <button className="btn" onClick={handleSync} disabled={syncing} style={{ whiteSpace: "nowrap" }}>
        {syncing ? "Syncing…" : "Sync Credit Data from Tally"}
      </button>
    </div>
  );
}

function BillsReceivableRefreshPanel({ showToast }) {
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await api.refreshSoa();
      showToast("Bills Receivable cache refreshed successfully.");
    } catch (e) {
      showToast(`Refresh failed: ${e.message}`, false);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Bills Receivable Cache</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Force-refresh the Bills Receivable cache from Tally. Normally auto-refreshes every 6 hours — use this if outstanding balances need to be updated immediately.
      </p>
      <button className="btn" onClick={handleRefresh} disabled={refreshing} style={{ whiteSpace: "nowrap" }}>
        {refreshing ? "Refreshing…" : "Refresh Bills Receivable Cache"}
      </button>
    </div>
  );
}

function CollectionForecastSnapshotPanel({ showToast }) {
  const [snapshotting, setSnapshotting] = useState(false);

  async function handleSnapshot() {
    setSnapshotting(true);
    try {
      await api.snapshotCollectionForecast();
      showToast("Collection forecast snapshot saved — targets are now locked for this month.");
    } catch (e) {
      showToast(`Snapshot failed: ${e.message}`, false);
    } finally {
      setSnapshotting(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Collection Forecast Snapshot</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Lock the collection targets for the current month by snapshotting outstanding bills due this month from Tally.
        Normally runs automatically at midnight on the 1st — use this to lock targets immediately or re-snapshot after a data correction.
      </p>
      <button className="btn" onClick={handleSnapshot} disabled={snapshotting} style={{ whiteSpace: "nowrap" }}>
        {snapshotting ? "Snapshotting…" : "Snapshot Collection Targets (This Month)"}
      </button>
    </div>
  );
}

function MonthlyReportRefreshPanel({ showToast }) {
  const opts = useRef(buildMonthOptions()).current;
  const [selected, setSelected]   = useState(`${opts[0].year}-${opts[0].month}`);
  const [refreshing, setRefreshing] = useState(false);

  async function handleRefresh() {
    const [y, m] = selected.split("-").map(Number);
    setRefreshing(true);
    try {
      await api.refreshMonthlyReport(y, m);
      showToast(`Monthly report for ${MONTHS_SHORT[m - 1]} ${y} refreshed successfully.`);
    } catch (e) {
      showToast(`Refresh failed: ${e.message}`, false);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div style={{
      border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px",
      marginBottom: 24, background: "#fafafa",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Monthly Report Cache</div>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#6b7280" }}>
        Force-refresh the cached snapshot for any month, including the current one. Auto-syncs nightly — use this for an immediate update.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          className="btn"
          style={{ cursor: "pointer" }}>
          {opts.map(o => (
            <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>
              {MONTHS_SHORT[o.month - 1]} {o.year}
            </option>
          ))}
        </select>
        <button className="btn" onClick={handleRefresh} disabled={refreshing} style={{ whiteSpace: "nowrap" }}>
          {refreshing ? "Refreshing…" : "Refresh Report Cache"}
        </button>
      </div>
    </div>
  );
}

export function PageCrmGroups() {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [saving,         setSaving]         = useState(null);
  const [toast,          setToast]          = useState(null);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }

  async function load() {
    setLoading(true); setError(null);
    try { setData(await api.loadCrmGroups()); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function handleAssign(companyId, groupName, userEmail) {
    if (!userEmail) return;
    const key = `${companyId}::${groupName}`;
    setSaving(key);
    try {
      await api.assignCrmGroup(companyId, groupName, userEmail);
      setData(prev => updateGroup(prev, companyId, groupName, g => ({
        ...g, status: "ACTIVE", userEmail,
        userName: prev.users.find(u => u.email === userEmail)?.name ?? userEmail,
      })));
      showToast("Group assigned");
    } catch (e) { alert("Failed to assign: " + e.message); }
    finally { setSaving(null); }
  }

  async function handleIgnore(companyId, groupName) {
    const key = `${companyId}::${groupName}`;
    setSaving(key);
    try {
      await api.ignoreCrmGroup(companyId, groupName);
      setData(prev => updateGroup(prev, companyId, groupName, g => ({
        ...g, status: "IGNORED", userEmail: null, userName: null,
      })));
      showToast("Group ignored");
    } catch (e) { alert("Failed to ignore: " + e.message); }
    finally { setSaving(null); }
  }

  async function handleReset(companyId, groupName) {
    const key = `${companyId}::${groupName}`;
    setSaving(key);
    try {
      await api.resetCrmGroup(companyId, groupName);
      setData(prev => updateGroup(prev, companyId, groupName, g => ({
        ...g, status: "PENDING", userEmail: null, userName: null,
      })));
      showToast("Group reset");
    } catch (e) { alert("Failed to reset: " + e.message); }
    finally { setSaving(null); }
  }

  const pendingTotal = data?.companies?.reduce(
    (sum, c) => sum + c.groups.filter(g => g.status === "PENDING").length, 0) ?? 0;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1100 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Sales Module Control Panel</h2>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 14 }}>
            Assign Tally groups to salespeople, sync invoice dates, and manage monthly report cache.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-primary" onClick={load} disabled={loading} style={{ whiteSpace: "nowrap" }}>
            {loading ? "Loading from Tally…" : data ? "Refresh Groups" : "Load from Tally"}
          </button>
        </div>
      </div>

      {toast && (
        <div style={{
          padding: "10px 14px", marginBottom: 12, borderRadius: 8, fontSize: 13,
          background: toast.ok ? "#f0fdf4" : "#fef2f2",
          border:     `1px solid ${toast.ok ? "#bbf7d0" : "#fecaca"}`,
          color:      toast.ok ? "#15803d"  : "#b91c1c",
        }}>
          {toast.ok ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      {pendingTotal > 0 && (
        <div style={{
          background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8,
          padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#92400e",
        }}>
          {pendingTotal} group{pendingTotal !== 1 ? "s" : ""} awaiting assignment.
          Assign a salesperson or mark as Ignored.
        </div>
      )}

      {error && (
        <div className="alert-warn" style={{ padding: 10, marginBottom: 16 }}>{error}</div>
      )}

      <TallyLedgerCachePanel showToast={showToast} onRefreshed={load} />
      <SyncInvoiceDatesPanel showToast={showToast} />
      <CreditDataSyncPanel showToast={showToast} />
      <BillsReceivableRefreshPanel showToast={showToast} />
      <CollectionForecastSnapshotPanel showToast={showToast} />
      <MonthlyReportRefreshPanel showToast={showToast} />

      {data?.companies?.map(company => (
        <CompanySection
          key={company.companyId}
          company={company}
          users={data.users}
          onAssign={handleAssign}
          onIgnore={handleIgnore}
          onReset={handleReset}
          saving={saving}
        />
      ))}
    </div>
  );
}

function updateGroup(data, companyId, groupName, updater) {
  return {
    ...data,
    companies: data.companies.map(c =>
      c.companyId !== companyId ? c : {
        ...c,
        groups: c.groups.map(g => g.groupName === groupName ? updater(g) : g),
      }
    ),
  };
}
